<template>
  <div>
    <h2 class="intro-y text-lg font-medium mt-10">Daily Snapshots</h2>
    <div class="grid grid-cols-12 gap-6 mt-5">
      <div
        class="intro-y col-span-12 flex flex-wrap sm:flex-nowrap items-center mt-2"
      >
        <div class="hidden md:block mx-auto text-gray-600">
          {{ dailyReportRowData.results.length }} Total Daily Snapshots
        </div>
      </div>
    </div>
    <p v-if="configurationRequired" class="mt-5">
      Set the {{ eventYear }} daily report dates and {{ previousEventYear }}
      comparison dates in Event Configuration to view this report.
    </p>
    <p v-if="reportError" class="mt-5 text-red">{{ reportError }}</p>
    <div class="intro-y mt-5 h-full col-span-12">
      <div class="mb-2 text-right">
        <button class="btn btn-outline mr-6" @click="resetFilters()">
          Reset Filters
        </button>
        <button
          class="btn btn-outline"
          :disabled="!gridApi || configurationRequired || Boolean(reportError)"
          @click="exportToCSV()"
        >
          Download CSV export file
        </button>
      </div>
      <AgGridVue
        v-if="!configurationRequired && !reportError"
        class="ag-theme-alpine w-full the-report-grid"
        :column-defs="columnDefs"
        :row-data="dailyReportRowData.results"
        :default-col-def="defaultColDef"
        @grid-ready="onGridReady"
      >
      </AgGridVue>
    </div>
  </div>
</template>
<script>
import {
  computed,
  defineComponent,
  onMounted,
  reactive,
  ref,
  shallowRef
} from "vue";
import { AgGridVue } from "ag-grid-vue3";
import { getDailyReports } from "@/api/reports.js";
import { useStore } from "@/store";
import { chicagoDate, comparisonPercent } from "@/utils/daily-report";

export default defineComponent({
  components: { AgGridVue },
  setup() {
    const store = useStore();
    const dailyReportRowData = reactive({ results: [] });
    const eventYear = ref(store.getters["main/appYear"]);
    const previousEventYear = ref(String(Number(eventYear.value) - 1));
    const configurationRequired = ref(false);
    const reportError = ref("");
    const gridApi = shallowRef(null);
    const defaultColDef = { minWidth: 50, filter: true, resizable: true };

    const setDailyReportData = async () => {
      reportError.value = "";
      try {
        const report = await getDailyReports();
        dailyReportRowData.results = report.results;
        eventYear.value = report.eventYear;
        previousEventYear.value = report.previousEventYear;
        configurationRequired.value = report.configurationRequired;
      } catch (error) {
        dailyReportRowData.results = [];
        reportError.value =
          "Unable to load daily snapshots. Use Reset Filters to retry.";
      }
    };
    onMounted(setDailyReportData);

    const dateColumn = (year, field) => ({
      headerName: `${year} Date`,
      field,
      sortable: true,
      filter: "agDateColumnFilter",
      filterParams: {
        comparator: (filterDate, value) => {
          const date = chicagoDate(value);
          if (!date) return -1;
          const [year, month, day] = date.split("-").map(Number);
          const time = new Date(year, month - 1, day).getTime();
          return Math.sign(time - filterDate.getTime());
        },
        browserDatePicker: true
      },
      valueFormatter: params => chicagoDate(params.value),
      pinned: "left"
    });
    const percentColumn = (title, metric) => ({
      headerName: `% ${title}`,
      valueGetter: params =>
        comparisonPercent(params.data.comparison[metric], params.data[metric])
    });
    const metrics = [
      ["OKC Total Raised", "okcTotalRaised"],
      ["Tulsa Total Raised", "tulsaTotalRaised"],
      ["Total Raised", "totalRaised"],
      ["OKC Total Teams", "okcTotalTeams"],
      ["Tulsa Total Teams", "tulsaTotalTeams"],
      ["Total Teams", "totalTeams"],
      ["OKC Total Participants", "okcTotalParticipants"],
      ["Tulsa Total Participants", "tulsaTotalParticipants"],
      ["Total Participants", "totalParticipants"]
    ];
    const columnDefs = computed(() => [
      dateColumn(eventYear.value, "comparison.createdAt"),
      dateColumn(previousEventYear.value, "createdAt"),
      {
        headerName: "Campaign Day",
        field: "campaignDay",
        filter: "agNumberColumnFilter"
      },
      percentColumn("Total Raised", "totalRaised"),
      percentColumn("Total Teams", "totalTeams"),
      percentColumn("OKC Total Raised", "okcTotalRaised"),
      percentColumn("OKC Total Teams", "okcTotalTeams"),
      percentColumn("Tulsa Total Raised", "tulsaTotalRaised"),
      percentColumn("Tulsa Total Teams", "tulsaTotalTeams"),
      ...metrics.flatMap(([title, metric]) => [
        {
          headerName: `${eventYear.value} ${title}`,
          field: `comparison.${metric}`,
          filter: "agNumberColumnFilter"
        },
        {
          headerName: `${previousEventYear.value} ${title}`,
          field: metric,
          filter: "agNumberColumnFilter"
        }
      ])
    ]);
    const onGridReady = params => {
      gridApi.value = params.api;
    };
    const exportToCSV = () => {
      if (!gridApi.value) return;
      gridApi.value.exportDataAsCsv({
        processCellCallback: params => {
          const field = params.column.getColDef().field || "";
          return field.endsWith("createdAt")
            ? chicagoDate(params.value)
            : params.value == null
            ? ""
            : params.value;
        }
      });
    };
    const resetFilters = async () => {
      if (gridApi.value) gridApi.value.setFilterModel(null);
      await setDailyReportData();
    };
    return {
      columnDefs,
      dailyReportRowData,
      onGridReady,
      exportToCSV,
      defaultColDef,
      resetFilters,
      eventYear,
      previousEventYear,
      configurationRequired,
      reportError,
      gridApi
    };
  }
});
</script>

<style scoped>
@import "~ag-grid-community/dist/styles/ag-grid.css";
@import "~ag-grid-community/dist/styles/ag-theme-alpine.css";
.the-report-grid {
  height: 800px;
}
</style>
